"use client";

import { c as _c } from "react/compiler-runtime";
import { createImageUrlBuilder } from "@sanity/image-url";
import { setElementVars } from "@vanilla-extract/dynamic";
import type { ImageField } from "library/sanity/assetMetadata";
import { stegaClean } from "next-sanity";
import { type SyntheticEvent, use, useEffect, useLayoutEffect, useRef, useState } from "react";
import { dataset, projectId } from "sanity/lib/api";
import type { SanityImageCrop, SanityImageHotspot } from "sanity.types";
import StaticImage, { type DefaultImageProps, EagerContext, prioritizeLoading, type StaticImageProps } from "../StaticImage";
import { useCombinedRefs } from "../useCombinedRefs";
import { hotspotObjectPositionVar, lqipFilterVar, lqipImageRenderingVar, lqipObjectFitVar, lqipVar, sanityImageClass } from "./SanityImage.css";
import { DefaultImg } from "data:text/javascript;base64,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";
// todo: ImageField and SanityImageData are the same shape?
export type SanityImageData<WithAlt extends "true" | "false"> = {
  asset?: {
    _ref: string;
  };
  crop?: SanityImageCrop;
  hotspot?: SanityImageHotspot;
  data: ImageField["data"] | null;
  alt?: string;
  willHaveAlt?: WithAlt;
};
type SanityProps = {
  src: SanityImageData<"false"> | null | undefined;
  alt: string;
} | {
  src: SanityImageData<"true"> | null | undefined;
  alt?: undefined;
};
export type SanityImageProps = SanityProps & {
  loading?: "eager" | "lazy" | "default";
  quality?: number;
  width?: number;
  height?: number;
} & Omit<DefaultImageProps, "srcSet">;
const urlBuilder = createImageUrlBuilder({
  projectId,
  dataset
});
const SRCSET_WIDTHS = [400, 800, 1200, 1600, 2400];
function checkLqipTransparency(lqip: string, onResult: (hasTransparency: boolean) => void): void {
  if (!lqip.startsWith("data:image/png;base64,")) {
    onResult(false);
    return;
  }
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d", {
      willReadFrequently: true
    });
    if (!ctx) return;
    ctx.drawImage(img, 0, 0);
    const {
      data
    } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < data.length; i += 4) {
      if ((data[i] ?? 255) < 255) {
        onResult(true);
        return;
      }
    }
    onResult(false);
  };
  img.src = lqip;
}
const isStringProps = (props: SanityImageProps | StaticImageProps): props is StaticImageProps & {
  src: string;
} => typeof props.src === "string";
const isNextProps = (props: SanityImageProps | StaticImageProps): props is Exclude<StaticImageProps, {
  src: string;
}> => !!props.src && !isStringProps(props) && ("default" in props.src || "src" in props.src);
export default function SanityUniversalImage(props) {
  const $ = _c(5);
  if (!props.src) {
    return null;
  }
  if (isStringProps(props) || isNextProps(props)) {
    let t0;
    if ($[0] !== props) {
      t0 = <StaticImage {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  const sanityProps = props as SanityImageProps;
  const t0 = sanityProps.src?.asset?._ref;
  let t1;
  if ($[2] !== sanityProps || $[3] !== t0) {
    t1 = <SanityImageCore key={t0} {...sanityProps} />;
    $[2] = sanityProps;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function SanityImageCore(props) {
  const $ = _c(56);
  let ref;
  let rest;
  let src;
  let t0;
  if ($[0] !== props) {
    ({
      src,
      ref,
      quality: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = ref;
    $[2] = rest;
    $[3] = src;
    $[4] = t0;
  } else {
    ref = $[1];
    rest = $[2];
    src = $[3];
    t0 = $[4];
  }
  const quality = t0 === undefined ? 90 : t0;
  const defaultEager = use(EagerContext);
  let t1;
  if ($[5] !== defaultEager || $[6] !== props.loading) {
    t1 = prioritizeLoading(props.loading, defaultEager);
    $[5] = defaultEager;
    $[6] = props.loading;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const prioritizedLoading = t1;
  const t2 = src?.asset?._ref;
  const t3 = src?.crop?.left;
  const t4 = src?.crop?.right;
  const t5 = src?.crop?.top;
  const t6 = src?.crop?.bottom;
  let t7;
  if ($[8] !== quality || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = [t2, t3, t4, t5, t6, quality];
    $[8] = quality;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const srcKey = t7.join(":");
  const [loadedKey, setLoadedKey] = useState(null);
  const loaded = loadedKey === srcKey;
  const [hasTransparency, setHasTransparency] = useState(false);
  const imgRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, imgRef);
  let t8;
  if ($[15] !== rest || $[16] !== srcKey) {
    t8 = event => {
      setLoadedKey(srcKey);
      rest.onLoad?.(event);
    };
    $[15] = rest;
    $[16] = srcKey;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleLoad = t8;
  let t10;
  let t9;
  if ($[18] !== loaded || $[19] !== srcKey) {
    t9 = () => {
      if (loaded) {
        return;
      }
      const el = imgRef.current;
      if (!el) {
        return;
      }
      if (el.complete && el.naturalWidth) {
        setLoadedKey(srcKey);
      }
    };
    t10 = [loaded, srcKey];
    $[18] = loaded;
    $[19] = srcKey;
    $[20] = t10;
    $[21] = t9;
  } else {
    t10 = $[20];
    t9 = $[21];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[22] !== src?.data?.lqip) {
    t11 = () => {
      const lqip = src?.data?.lqip;
      setHasTransparency(false);
      if (!lqip) {
        return;
      }
      let active = true;
      checkLqipTransparency(lqip, result => {
        if (active) {
          setHasTransparency(result);
        }
      });
      return () => {
        active = false;
      };
    };
    $[22] = src?.data?.lqip;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = src?.data?.lqip;
  let t13;
  if ($[24] !== t12) {
    t13 = [t12];
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  useEffect(t11, t13);
  const hotspotX = src?.hotspot?.x;
  const hotspotY = src?.hotspot?.y;
  const cropLeft = src?.crop?.left;
  const cropRight = src?.crop?.right;
  const cropTop = src?.crop?.top;
  const cropBottom = src?.crop?.bottom;
  const hasHotspot = hotspotX != null && hotspotY != null;
  const shouldTrackRenderedFit = hasHotspot || !loaded && !!src?.data?.lqip;
  let t14;
  let t15;
  if ($[26] !== cropBottom || $[27] !== cropLeft || $[28] !== cropRight || $[29] !== cropTop || $[30] !== hasHotspot || $[31] !== hotspotX || $[32] !== hotspotY || $[33] !== shouldTrackRenderedFit) {
    t14 = () => {
      if (!shouldTrackRenderedFit) {
        return;
      }
      const el_0 = imgRef.current;
      if (!el_0) {
        return;
      }
      let rafId = null;
      const scheduleCompute = () => {
        if (rafId != null) {
          return;
        }
        rafId = requestAnimationFrame(compute);
      };
      const compute = () => {
        rafId = null;
        const {
          objectFit: fit
        } = getComputedStyle(el_0);
        setElementVars(el_0, {
          [lqipObjectFitVar]: fit || "cover"
        });
        if (!hasHotspot || fit !== "cover") {
          setElementVars(el_0, {
            [hotspotObjectPositionVar]: "center"
          });
          return;
        }
        const l = cropLeft ?? 0;
        const r = cropRight ?? 0;
        const t = cropTop ?? 0;
        const b = cropBottom ?? 0;
        const cw = 1 - l - r;
        const ch = 1 - t - b;
        const hx = Math.max(0, Math.min(1, cw > 0 ? (hotspotX - l) / cw : 0.5));
        const hy = Math.max(0, Math.min(1, ch > 0 ? (hotspotY - t) / ch : 0.5));
        if (!el_0.naturalWidth || !el_0.naturalHeight) {
          return;
        }
        const containerW = el_0.offsetWidth;
        const containerH = el_0.offsetHeight;
        if (!containerW || !containerH) {
          return;
        }
        const s = Math.max(containerW / el_0.naturalWidth, containerH / el_0.naturalHeight);
        const scaledW = el_0.naturalWidth * s;
        const scaledH = el_0.naturalHeight * s;
        const ox = Math.max(containerW - scaledW, Math.min(0, containerW / 2 - scaledW * hx));
        const oy = Math.max(containerH - scaledH, Math.min(0, containerH / 2 - scaledH * hy));
        setElementVars(el_0, {
          [hotspotObjectPositionVar]: `${ox}px ${oy}px`
        });
      };
      const ro = new ResizeObserver(scheduleCompute);
      ro.observe(el_0);
      window.addEventListener("resize", scheduleCompute);
      el_0.addEventListener("load", scheduleCompute);
      scheduleCompute();
      return () => {
        if (rafId != null) {
          cancelAnimationFrame(rafId);
        }
        ro.disconnect();
        window.removeEventListener("resize", scheduleCompute);
        el_0.removeEventListener("load", scheduleCompute);
        setElementVars(el_0, {
          [hotspotObjectPositionVar]: "center",
          [lqipObjectFitVar]: "cover"
        });
      };
    };
    t15 = [shouldTrackRenderedFit, hasHotspot, hotspotX, hotspotY, cropLeft, cropRight, cropTop, cropBottom];
    $[26] = cropBottom;
    $[27] = cropLeft;
    $[28] = cropRight;
    $[29] = cropTop;
    $[30] = hasHotspot;
    $[31] = hotspotX;
    $[32] = hotspotY;
    $[33] = shouldTrackRenderedFit;
    $[34] = t14;
    $[35] = t15;
  } else {
    t14 = $[34];
    t15 = $[35];
  }
  useLayoutEffect(t14, t15);
  if (!src?.asset) {
    return null;
  }
  let imgSrc;
  let t16;
  if ($[36] !== quality || $[37] !== src.asset || $[38] !== src.crop) {
    const base = urlBuilder.image({
      _type: "image" as const,
      asset: src.asset,
      crop: src.crop
    }).quality(quality).auto("format");
    imgSrc = base.width(1600).url();
    t16 = SRCSET_WIDTHS.map(w => `${base.width(w).url()} ${w}w`).join(", ");
    $[36] = quality;
    $[37] = src.asset;
    $[38] = src.crop;
    $[39] = imgSrc;
    $[40] = t16;
  } else {
    imgSrc = $[39];
    t16 = $[40];
  }
  const srcSet = t16;
  const lqip_0 = !loaded && src.data?.lqip;
  let t17;
  if ($[41] !== hasTransparency || $[42] !== lqip_0) {
    t17 = lqip_0 ? {
      lqip: `url(${lqip_0})`,
      ...(hasTransparency && {
        lqipFilter: "blur(24px)" as const,
        lqipImageRendering: "pixelated" as const
      })
    } : undefined;
    $[41] = hasTransparency;
    $[42] = lqip_0;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const lqipProps = t17;
  const t18 = rest.alt ?? src.alt ?? "";
  let t19;
  if ($[44] !== t18) {
    t19 = stegaClean(t18);
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const t20 = props.width && props.height ? `${props.width}/${props.height}` : src.data?.aspectRatio ? String(src.data.aspectRatio) : undefined;
  let t21;
  if ($[46] !== combinedRef || $[47] !== handleLoad || $[48] !== imgSrc || $[49] !== lqipProps || $[50] !== prioritizedLoading || $[51] !== rest || $[52] !== srcSet || $[53] !== t19 || $[54] !== t20) {
    t21 = <DefaultImg {...rest} ref={combinedRef} alt={t19} loading={prioritizedLoading} onLoad={handleLoad} src={imgSrc} srcSet={srcSet} aspectRatio={t20} {...lqipProps} />;
    $[46] = combinedRef;
    $[47] = handleLoad;
    $[48] = imgSrc;
    $[49] = lqipProps;
    $[50] = prioritizedLoading;
    $[51] = rest;
    $[52] = srcSet;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  return t21;
}